import React, { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import VariantComparison from './VariantComparison';
import { toast } from 'sonner';

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
];

const AIGenerationModal = ({ 
  isOpen, 
  onClose, 
  product,
  workspaceId,
  onGenerated 
}) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [language, setLanguage] = useState('fr');
  const [customInstructions, setCustomInstructions] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [variants, setVariants] = useState([]);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);

  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleGenerate = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Veuillez sélectionner au moins une plateforme');
      return;
    }

    setIsGenerating(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/content/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            product_id: product.id,
            platforms: selectedPlatforms,
            language: language,
            num_variants: 3,
            custom_instructions: customInstructions || null,
            skip_cache: false,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Génération échouée');
      }

      const data = await response.json();
      setVariants(data.variants || []);
      toast.success('Contenu généré avec succès !');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.message || 'Erreur lors de la génération');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  const handleSelectVariant = (index) => {
    setSelectedVariantIndex(index);
  };

  const handleUseVariant = () => {
    if (selectedVariantIndex === null) {
      toast.error('Veuillez sélectionner une variante');
      return;
    }

    const selectedVariant = variants[selectedVariantIndex];
    if (onGenerated) {
      onGenerated(selectedVariant);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="ai-generation-modal" className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Génération IA - {product?.name}
          </DialogTitle>
          <DialogDescription>
            Générez des publications optimisées pour vos réseaux sociaux
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plateformes */}
            <div className="space-y-3">
              <Label>Plateformes cibles *</Label>
              <div data-testid="platform-selector" className="grid grid-cols-2 gap-2">
                {PLATFORMS.map((platform) => (
                  <div key={platform.value} className="flex items-center space-x-2">
                    <Checkbox
                      data-testid={`platform-checkbox-${platform.value}`}
                      id={`platform-${platform.value}`}
                      checked={selectedPlatforms.includes(platform.value)}
                      onCheckedChange={() => handlePlatformToggle(platform.value)}
                    />
                    <label
                      htmlFor={`platform-${platform.value}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {platform.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Langue */}
            <div className="space-y-3">
              <Label htmlFor="language">Langue</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="language-selector" id="language">
                  <SelectValue placeholder="Sélectionnez une langue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">Anglais</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Instructions personnalisées */}
          <div className="space-y-3">
            <Label htmlFor="instructions">Instructions personnalisées (optionnel)</Label>
            <Textarea
              data-testid="custom-instructions"
              id="instructions"
              placeholder="Ex: Ton humoristique, mettre l'accent sur l'éco-responsabilité, inclure un appel à l'action..."
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              rows={3}
            />
          </div>

          {/* Génération Button */}
          {variants.length === 0 && (
            <Button
              data-testid="generate-content-button"
              onClick={handleGenerate}
              disabled={isGenerating || selectedPlatforms.length === 0}
              className="w-full gap-2"
              size="lg"
            >
              {isGenerating ? (
                'Génération en cours...'
              ) : (
                <>
                  <Sparkles className="h-5 w-5" />
                  Générer les variantes
                </>
              )}
            </Button>
          )}

          {/* Variantes */}
          {variants.length > 0 && (
            <>
              <VariantComparison
                variants={variants}
                selectedIndex={selectedVariantIndex}
                onSelect={handleSelectVariant}
                onRegenerate={handleRegenerate}
                isLoading={isGenerating}
              />

              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  data-testid="use-variant-button"
                  onClick={handleUseVariant}
                  disabled={selectedVariantIndex === null}
                  className="flex-1"
                  size="lg"
                >
                  Utiliser cette variante
                </Button>
                <Button
                  data-testid="cancel-button"
                  onClick={onClose}
                  variant="outline"
                  size="lg"
                >
                  Annuler
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIGenerationModal;
